<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import {
  computed, nextTick, ref, watch,
} from 'vue';

// eslint-disable-next-line import/order
import type { MicroInverters } from '~/types/equipment';

import ModalForm from '~/components/shared/ModalForm.vue';
import SearchableSelect from '~/components/shared/SearchableSelect.vue';
import TagInput from '~/components/shared/TagInput.vue';
import {
  EQUIPMENT_LABELS,
  FORM_HELP,
  FORM_PLACEHOLDERS,
  getSkusDuplicateMessage,
  MODAL_TITLES,
  PHASE_OPTIONS,
} from '~/constants/equipment';
import { useBrandsOptions } from '~/shared/hooks/useBrands';

import { useCreateMicroInverter, useMicroInverterById, useUpdateMicroInverter } from '../queries/useMicroInvertersCrud';
import { microInverterSchema } from '../schemas/microInverterSchema';

export interface MicroInverterModalProps {
  microInverterData?: MicroInverters.MicroInverter | null;
  microInverterId?: null | number;
  mode: 'create' | 'edit' | 'view';
  modelValue: boolean;
}

const props = withDefaults(defineProps<MicroInverterModalProps>(), {
  microInverterData: null,
  microInverterId: null,
});

const emit = defineEmits<{
  'success': [];
  'update:modelValue': [value: boolean];
}>();

const selectedMicroInverterId = ref<null | number>(props.microInverterId);
const modalFormRef = ref();

const { isLoading: isLoadingBrands, options: brandsOptions } = useBrandsOptions('microinverters');

const { data: fetchedMicroInverterData } = useMicroInverterById(selectedMicroInverterId);

const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
  setValues,
} = useForm({
  initialValues: {
    brand: '',
    countKey: 0,
    is_combo_conecte: false,
    is_enabled: true,
    isHybrid: false,
    model: '',
    overload: 0,
    phaseId: 1,
    power: 0,
    skus: null,
    tension: 0,
  },
  validateOnMount: false,
  validationSchema: toTypedSchema(microInverterSchema),
});

const fieldOptions = {
  validateOnBlur: true,
  validateOnChange: false,
  validateOnInput: true,
  validateOnModelUpdate: false,
};

const [brand] = defineField('brand', fieldOptions);
const [model, modelProps] = defineField('model', fieldOptions);
const [power, powerProps] = defineField('power', fieldOptions);
const [tension, tensionProps] = defineField('tension', fieldOptions);
const [countKey, countKeyProps] = defineField('countKey', fieldOptions);
const [phaseId, phaseIdProps] = defineField('phaseId', fieldOptions);
const [isHybrid, isHybridProps] = defineField('isHybrid', fieldOptions);
const [isComboConecte, isComboConecteProps] = defineField('is_combo_conecte', fieldOptions);
const [overload, overloadProps] = defineField('overload', fieldOptions);
const [skus] = defineField('skus', fieldOptions);
const [isEnabled, isEnabledProps] = defineField('is_enabled', fieldOptions);

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const isCreateMode = computed(() => props.mode === 'create');
const isEditMode = computed(() => props.mode === 'edit');

const modalTitle = computed(() => {
  if (isCreateMode.value) return MODAL_TITLES.MICROINVERTER.CREATE;
  if (isEditMode.value) return MODAL_TITLES.MICROINVERTER.EDIT;
  return MODAL_TITLES.MICROINVERTER.VIEW;
});

const createMutation = useCreateMicroInverter();
const updateMutation = useUpdateMicroInverter();

const modalIsLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    if (props.mode === 'create') {
      resetForm();
    } else {
      selectedMicroInverterId.value = props.microInverterId;

      if (props.mode === 'edit' || props.mode === 'view') {
        if (props.microInverterData) {
          const data = props.microInverterData;
          nextTick(() => {
            setValues({
              brand: data.brand || '',
              countKey: data.count_key || 1,
              is_combo_conecte: data.is_combo_conecte ?? false,
              is_enabled: data.is_enabled ?? true,
              isHybrid: data.is_hybrid || false,
              model: data.model || '',
              overload: data.overload || 0,
              phaseId: data.phase_id || 1,
              power: data.power || 0,
              skus: data.skus?.length ? data.skus : null,
              tension: data.tension || 1,
            });
          });
        }
      }
    }
  }
});

watch(() => fetchedMicroInverterData.value, (freshData) => {
  if (freshData && (props.mode === 'edit' || props.mode === 'view') && !props.microInverterData) {
    nextTick(() => {
      setValues({
        brand: freshData.brand || '',
        countKey: freshData.count_key || 1,
        is_combo_conecte: freshData.is_combo_conecte ?? false,
        is_enabled: freshData.is_enabled ?? true,
        isHybrid: freshData.is_hybrid || false,
        model: freshData.model || '',
        overload: freshData.overload || 0,
        phaseId: freshData.phase_id || 1,
        power: freshData.power || 0,
        skus: freshData.skus?.length ? freshData.skus : null,
        tension: freshData.tension || 1,
      });
    });
  }
}, { immediate: false });

const handleSave = handleSubmit(async (values) => {
  try {
    const payload = {
      brand: values.brand,
      count_key: values.countKey,
      is_combo_conecte: values.is_combo_conecte,
      is_hybrid: values.isHybrid,
      model: values.model,
      overload: values.overload,
      phase_id: values.phaseId,
      power: values.power,
      skus: values.skus?.length ? values.skus : [],
      tension: values.tension,
    };

    if (isCreateMode.value) {
      await createMutation.mutateAsync(payload);
    } else {
      const id = props.microInverterData?.id || selectedMicroInverterId.value;
      if (id) {
        await updateMutation.mutateAsync({
          data: { ...payload, is_enabled: values.is_enabled },
          id,
        });
      }
    }

    emit('success');
    modalFormRef.value?.resetEditingState();
    isVisible.value = false;
  } catch (error) {
    // Error handled by mutation
  }
});

const handleCancel = () => {
  isVisible.value = false;
};

const handleEdit = () => {
  emit('update:modelValue', false);
  nextTick(() => {
    emit('update:modelValue', true);
  });
};
</script>

<template>
  <ModalForm
    ref="modalFormRef"
    v-model="isVisible"
    :title="modalTitle"
    :mode="mode"
    :loading="modalIsLoading"
    size="lg"
    @save="handleSave"
    @edit="handleEdit"
    @cancel="handleCancel"
  >
    <template #default="{ isViewMode: modalIsViewMode, isLoading: modalIsLoading }">
      <div class="row">
        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="brand">{{ EQUIPMENT_LABELS.BRAND }}</label>
            <SearchableSelect
              id="brand"
              v-model="brand"
              :options="brandsOptions"
              :loading="isLoadingBrands"
              :disabled="modalIsViewMode || modalIsLoading"
              :class="{ 'is-invalid': errors.brand }"
              :placeholder="FORM_PLACEHOLDERS.BRAND_SELECT"
              loading-text="Carregando marcas..."
              no-options-text="Nenhuma marca encontrada"
            />
            <div v-if="errors.brand" class="invalid-feedback">
              {{ errors.brand }}
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="model">{{ EQUIPMENT_LABELS.MODEL }}</label>
            <input
              id="model"
              v-model="model"
              v-bind="modelProps"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.model }"
              :disabled="modalIsViewMode || modalIsLoading"
              :placeholder="FORM_PLACEHOLDERS.MODEL"
            />
            <div v-if="errors.model" class="invalid-feedback">
              {{ errors.model }}
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <div class="mb-3">
            <label class="form-label required" for="power">{{ EQUIPMENT_LABELS.POWER }}</label>
            <input
              id="power"
              v-model="power"
              v-bind="powerProps"
              type="number"
              min="0.1"
              max="999"
              step="0.1"
              class="form-control"
              :class="{ 'is-invalid': errors.power }"
              :disabled="modalIsViewMode || modalIsLoading"
              :placeholder="FORM_PLACEHOLDERS.POWER"
            />
            <div v-if="errors.power" class="invalid-feedback">
              {{ errors.power }}
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="mb-3">
            <label class="form-label required" for="tension">{{ EQUIPMENT_LABELS.TENSION }}</label>
            <input
              id="tension"
              v-model="tension"
              v-bind="tensionProps"
              type="number"
              min="1"
              class="form-control"
              :class="{ 'is-invalid': errors.tension }"
              :disabled="modalIsViewMode || modalIsLoading"
              :placeholder="FORM_PLACEHOLDERS.TENSION"
            />
            <div v-if="errors.tension" class="invalid-feedback">
              {{ errors.tension }}
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="mb-3">
            <label class="form-label required" for="countKey">{{ EQUIPMENT_LABELS.COUNT_KEY }}</label>
            <input
              id="countKey"
              v-model="countKey"
              v-bind="countKeyProps"
              type="number"
              min="1"
              class="form-control"
              :class="{ 'is-invalid': errors.countKey }"
              :disabled="modalIsViewMode || modalIsLoading"
              :placeholder="FORM_PLACEHOLDERS.COUNT_KEY"
            />
            <div v-if="errors.countKey" class="invalid-feedback">
              {{ errors.countKey }}
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <div class="mb-3">
            <label class="form-label required" for="phaseId">{{ EQUIPMENT_LABELS.PHASE }}</label>
            <select
              id="phaseId"
              v-model="phaseId"
              v-bind="phaseIdProps"
              class="form-select"
              :class="{ 'is-invalid': errors.phaseId }"
              :disabled="modalIsViewMode || modalIsLoading"
            >
              <option
                v-for="option in PHASE_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <div v-if="errors.phaseId" class="invalid-feedback">
              {{ errors.phaseId }}
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="mb-3">
            <label class="form-label required" for="isHybrid">{{ EQUIPMENT_LABELS.TYPE }}</label>
            <select
              id="isHybrid"
              v-model="isHybrid"
              v-bind="isHybridProps"
              class="form-select"
              :class="{ 'is-invalid': errors.isHybrid }"
              :disabled="modalIsViewMode || modalIsLoading"
            >
              <option :value="false">Não Híbrido</option>
              <option :value="true">Híbrido</option>
            </select>
            <div v-if="errors.isHybrid" class="invalid-feedback">
              {{ errors.isHybrid }}
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="mb-3">
            <label class="form-label required" for="isComboConecte">Combo Conecte</label>
            <select
              id="isComboConecte"
              v-model="isComboConecte"
              v-bind="isComboConecteProps"
              class="form-select"
              :class="{ 'is-invalid': errors.is_combo_conecte }"
              :disabled="modalIsViewMode || modalIsLoading"
            >
              <option :value="false">Não</option>
              <option :value="true">Sim</option>
            </select>
            <div v-if="errors.is_combo_conecte" class="invalid-feedback">
              {{ errors.is_combo_conecte }}
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="mb-3">
            <label class="form-label required" for="overload">{{ EQUIPMENT_LABELS.OVERLOAD }}</label>
            <input
              id="overload"
              v-model="overload"
              v-bind="overloadProps"
              type="number"
              min="0"
              max="5"
              step="0.1"
              class="form-control"
              :class="{ 'is-invalid': errors.overload }"
              :disabled="modalIsViewMode || modalIsLoading"
              :placeholder="FORM_PLACEHOLDERS.OVERLOAD"
            />
            <div v-if="errors.overload" class="invalid-feedback">
              {{ errors.overload }}
            </div>
            <small v-else class="form-text text-muted">
              Digite o valor em porcentagem (ex: 2 para 100%, 1.98 para 98%). Máximo permitido: 5
            </small>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="mb-3">
            <TagInput
              id="skus"
              v-model="skus"
              :label="EQUIPMENT_LABELS.SKUS"
              :placeholder="FORM_PLACEHOLDERS.SKUS"
              :help-text="FORM_HELP.SKUS"
              item-label="SKU"
              :duplicate-message="getSkusDuplicateMessage"
              :disabled="modalIsViewMode || modalIsLoading"
              :error="errors.skus"
              :invalid="!!errors.skus"
            />
          </div>
        </div>
      </div>

      <div v-if="!isCreateMode" class="row">
        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="isEnabled">{{ EQUIPMENT_LABELS.STATUS }}</label>
            <select
              id="isEnabled"
              v-model="isEnabled"
              v-bind="isEnabledProps"
              class="form-select"
              :class="{ 'is-invalid': errors.is_enabled }"
              :disabled="modalIsViewMode || modalIsLoading"
            >
              <option :value="true">Ativado</option>
              <option :value="false">Desativado</option>
            </select>
            <div v-if="errors.is_enabled" class="invalid-feedback">
              {{ errors.is_enabled }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </ModalForm>
</template>

<style scoped lang="scss">
.form-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border-radius: 0.375rem;

  &:focus {
    border-color: #86b7fe;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  }

  &.is-invalid {
    border-color: #dc3545;

    &:focus {
      border-color: #dc3545;
      box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
    }
  }
}

.invalid-feedback {
  display: block;
  font-size: 0.875rem;
  color: #dc3545;
  margin-top: 0.25rem;
}
</style>
